import {
  getMemberType,
  isMemberAdmin,
  compareMemberTypes,
  getMembershipFor,
} from './memberships';
import {
  isMember as isWorkspaceMember,
  isMemberAdmin as isMemberWorkspaceAdmin,
} from '@trello/organizations';
import { ProductFeatures } from '@trello/product-features';
import type { Board, Enterprise, Member, Workspace } from './types';

export const canEdit = (
  member: Pick<
    Member,
    'id' | 'idPremOrgsAdmin' | 'idEnterprisesAdmin' | 'memberType'
  >,
  board: Pick<Board, 'prefs' | 'memberships' | 'organization'>,
  organization: Parameters<typeof isWorkspaceMember>[1] | null,
  enterprise: Parameters<typeof isWorkspaceMember>[2] | null,
) => {
  const memberType = getMemberType(member, board);

  const isWorkspaceAdmin =
    organization && isMemberWorkspaceAdmin(member, organization, enterprise);

  if ('observer' === memberType && !isWorkspaceAdmin) {
    return false;
  }

  // existing board member or the org/ent admin can edit
  if (['admin', 'normal'].includes(memberType) || isWorkspaceAdmin) {
    return true;
  }

  // non board members
  if (board.prefs?.selfJoin && organization && !board.prefs?.isTemplate) {
    if (isWorkspaceMember(member, organization, enterprise)) {
      return true;
    }
  }
  return false;
};

// Converted from app/scripts/models/board.js
export const canInviteMembers = (
  member: Pick<
    Member,
    'id' | 'idPremOrgsAdmin' | 'idEnterprisesAdmin' | 'memberType'
  >,
  board: Pick<Board, 'prefs' | 'memberships' | 'organization'>,
) => {
  const workspace = board.organization;

  // If member isn't in workspace, they cannot invite others if the workspace has restricted invites
  if (
    (!workspace || !isWorkspaceMember(member, workspace)) &&
    !board.prefs?.canInvite
  )
    return false;

  if (
    board.prefs?.invitations === 'admins' &&
    !(workspace && isMemberAdmin(member, board))
  )
    return false;

  const memberType = getMemberType(member, board);

  return (
    memberType === 'admin' ||
    (board.prefs?.invitations === 'members' && memberType === 'normal')
  );
};

// Converted from app/scripts/models/member.js
export const canRemoveMember = (
  me: Pick<
    Member,
    'id' | 'idPremOrgsAdmin' | 'idEnterprisesAdmin' | 'memberType'
  >,
  subject: Pick<
    Member,
    'id' | 'idPremOrgsAdmin' | 'idEnterprisesAdmin' | 'memberType'
  >,
  board: Pick<Board, 'memberships' | 'members' | 'organization'>,
) => {
  const workspace = board.organization;
  // If there are no other explicit board admins, also check if this board is in a paid workspace and has a member who is a workspace admin
  const boardHasAdminOtherThanSubject =
    board.memberships.some(
      ({ idMember, memberType }) =>
        idMember !== subject.id && memberType === 'admin',
    ) ||
    (workspace &&
      ProductFeatures.hasProduct(workspace.products?.[0]) &&
      board.members?.some(
        (boardMember) =>
          boardMember.id &&
          boardMember.id !== subject.id &&
          isMemberAdmin(boardMember as Member, board),
      ));

  const boardHasMoreThanOneMember = board.memberships.length > 1;
  const amAdmin = isMemberAdmin(me, board);
  const amSubject = me.id === subject.id;

  if (boardHasMoreThanOneMember) {
    if (boardHasAdminOtherThanSubject && (amAdmin || amSubject)) {
      return true;
    }

    const meMemberType = getMemberType(subject, board, false);
    if (
      !amSubject &&
      meMemberType !== 'org' &&
      canInviteMembers(me, board) &&
      compareMemberTypes(getMemberType(me, board), meMemberType) >= 0
    ) {
      return true;
    }
  }
  return false;
};

export const canRename = (
  member: Pick<
    Member,
    'id' | 'idPremOrgsAdmin' | 'idEnterprisesAdmin' | 'memberType'
  >,
  board: Pick<Board, 'memberships'>,
  organization: Parameters<typeof canEdit>[2],
  enterprise: Parameters<typeof canEdit>[3],
) => {
  if (
    // Member is a board admin
    getMemberType(member, board) === 'admin' ||
    // Member is a workspace admin
    (organization && isMemberWorkspaceAdmin(member, organization, enterprise))
  ) {
    return true;
  } else {
    return false;
  }
};

export const allowsSelfJoin = (board: Pick<Board, 'prefs'>) => {
  return (board.prefs?.selfJoin ?? false) && !board.prefs?.isTemplate;
};

/**
 * This ports the `canJoin` method from `app/scripts/models/board.js`, which
 * relied on model cache data.
 */
export const canJoin = (
  workspace: Pick<
    Workspace,
    'id' | 'idEnterprise' | 'enterprise' | 'memberships'
  >,
  member: Pick<
    Member,
    'id' | 'idPremOrgsAdmin' | 'idEnterprisesAdmin' | 'memberType'
  >,
  board: Pick<Board, 'memberships' | 'organization' | 'prefs' | 'idEnterprise'>,
  enterprise?: Pick<Enterprise, 'idAdmins'> & { id: string },
) => {
  if (!workspace && !board.idEnterprise) {
    return false;
  }

  // Any board member type (admin, normal, or observer) can't join a board
  const memberType = getMembershipFor(board, member.id);
  if (memberType) {
    return false;
  }

  const isPremOrgAdmin = isMemberWorkspaceAdmin(member, workspace);
  const selfJoin = allowsSelfJoin(board);

  const isEnterpriseBoard = !!board.idEnterprise || !!workspace.enterprise;

  const isOrgMember = isWorkspaceMember(member, workspace, enterprise);

  return (
    isPremOrgAdmin ||
    (selfJoin && isOrgMember) ||
    (isEnterpriseBoard &&
      enterprise &&
      enterprise.idAdmins?.includes(member.id))
  );
};
